import { useEffect, useLayoutEffect, useRef } from 'react'

export type VisibilityAwareIntervalOptions = {
  /**
   * Gates the interval on component conditions. When false, the interval is
   * cleared and does not run. Default: `true`.
   */
  enabled?: boolean
  /**
   * When true, calls `callback` once immediately upon mounting / enabling
   * **only if the document is currently visible**.
   * If the document starts hidden, the immediate mount call is skipped, and
   * the callback will instead fire on return to visibility. Default: `false`.
   */
  immediate?: boolean
}

/**
 * Runs `callback` on a repeating `ms` interval, but **only while the tab is
 * visible**. When the tab becomes hidden the interval is cleared; when it
 * becomes visible again the callback fires once immediately (refresh-on-return)
 * and the interval restarts.
 *
 * `callback` identity is kept current via a ref (`useLayoutEffect`) so changing
 * callback identity does not restart the interval. `ms` and `enabled` drive
 * the interval lifecycle — changing `ms` restarts the interval at the new period.
 *
 * Pass `immediate: true` (via options) if you need the callback to run immediately
 * on mount/enable when visible.
 */
export const useVisibilityAwareInterval = (
  callback: () => void,
  ms: number,
  optionsOrEnabled?: boolean | VisibilityAwareIntervalOptions,
): void => {
  const cbRef = useRef(callback)

  // Keep callback ref current using useLayoutEffect so it is always up to date
  // before any interval callback fires, without restarting the interval.
  useLayoutEffect(() => {
    cbRef.current = callback
  })

  const enabled = typeof optionsOrEnabled === 'boolean'
    ? optionsOrEnabled
    : (optionsOrEnabled?.enabled ?? true)

  const immediate = typeof optionsOrEnabled === 'object' && optionsOrEnabled !== null
    ? (optionsOrEnabled.immediate ?? false)
    : false

  useEffect(() => {
    if (!enabled || typeof window === 'undefined' || typeof document === 'undefined') return

    let timerId: number | null = null

    const start = () => {
      if (timerId !== null) return
      timerId = window.setInterval(() => cbRef.current(), ms)
    }

    const stop = () => {
      if (timerId === null) return
      window.clearInterval(timerId)
      timerId = null
    }

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        stop()
      } else {
        cbRef.current() // refresh-on-return
        start()
      }
    }

    // Only start the interval if the tab is already visible.
    if (document.visibilityState !== 'hidden') {
      if (immediate) {
        cbRef.current()
      }
      start()
    }

    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => {
      stop()
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [enabled, ms, immediate])
}
